<template>
  <div class="flex flex-col w-full gap-2 relative overflow-y-auto h-full">
  <CustomerBalance :customerId="clientId" class="mb-2 flex-none" @changed="loadPaymentsHistory" />
  <div v-if="isLoadingPayments" class="w-full flex flex-col gap-4">
    <Skeleton v-for="i in [1,2,3]" height="4rem" />
  </div>
  <div v-else class="flex flex-col w-full gap-2 relative">
    <div v-if="allPayments.length > 0" class="flex flex-col w-full">
      <div
        v-wave
        v-for="(payment, idx) of allPayments"
        :key="payment._id"
        :class="['flex items-start gap-2  py-3 cursor-pointer', idx !== allPayments.length - 1 && 'border-b']" @click="selectPayment(payment)">
        <div class="flex flex-row w-full items-center">
          <div class="flex flex-col gap-1">
            <span class="font-medium">{{ getTitle(payment) }}</span>
            <span v-if="payment.chargedDate" class="text-xs text-gray-400 text-right">חויב בתאריך {{
                DateTime.fromISO(payment.chargedDate as string).toLocaleString(DateTime.DATE_MED, { locale: 'he-IL' })
              }}</span>
            <span v-else class="text-xs text-gray-400 text-right">נשלח בתאריך {{
                DateTime.fromISO(payment.createdAt as string).toLocaleString(DateTime.DATE_MED, { locale: 'he-IL' })
              }}</span>
            <div v-if="payment.ezCountInvoiceLink" class="flex justify-start gap-2 mt-1">
              <span class="text-sm text-right">₪{{ payment.paidPrice ?? payment.price }}</span>
              <span v-if="isRefunded(payment)"
                    class="text-left text-xs text-orange-500 flex gap-1 items-center px-2 rounded bg-orange-50">
                {{ payment.status === PaymentRequestStatus.PartiallyRefunded ? 'הוחזר חלקית' : 'הוחזר' }}
              </span>
              <span v-if="payment.status === PaymentRequestStatus.Success"
                    class="text-left text-xs text-green-500 flex gap-1 items-center px-2 rounded bg-green-50">
              <span>שולם</span>
              <i class="pi pi-check" style="font-size: 0.5rem" />
            </span>
              <span
                v-else-if="payment.status === PaymentRequestStatus.InProgress || payment.status === PaymentRequestStatus.Sent"
                class="text-left  text-xs text-gray-500 flex gap-1 items-center px-2 rounded bg-gray-50">
              <span>{{ payment.status === PaymentRequestStatus.InProgress ? 'בתהליך' : 'נשלחה בקשה' }}</span>
              <i class="pi pi-check" style="font-size: 0.5rem" />
            </span>
            </div>
          </div>
          <a v-if="payment.ezCountInvoiceLink" class="mr-auto" :href="payment.ezCountInvoiceLink" target="_blank">
            <i class="pi pi-download p-1" />
          </a>
          <div v-else class="mr-auto flex flex-col items-end gap-1">
            <!-- What was actually charged (a saved card can be charged less than its consented price) -->
            <span> ₪{{ payment.paidPrice ?? payment.price }} </span>
            <span v-if="isRefunded(payment)"
                  class="text-left text-xs text-orange-500 flex gap-1 items-center px-2 rounded bg-orange-50">
              {{ payment.status === PaymentRequestStatus.PartiallyRefunded ? 'הוחזר חלקית' : 'הוחזר' }}
            </span>
            <span v-if="payment.status === PaymentRequestStatus.Success"
                  class="text-left text-xs text-green-500 flex gap-1 items-center px-2 rounded bg-green-50">
              <span>שולם</span>
              <i class="pi pi-check" style="font-size: 0.5rem" />
            </span>
            <span
              v-else-if="payment.status === PaymentRequestStatus.InProgress || payment.status === PaymentRequestStatus.Sent"
              class="text-left  text-xs text-gray-500 flex gap-1 items-center px-2 rounded bg-gray-50">
              <span>{{ payment.status === PaymentRequestStatus.InProgress ? 'בתהליך' : 'נשלחה בקשה' }}</span>
              <i class="pi pi-check" style="font-size: 0.5rem" />
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex w-full justify-center items-center flex-col gap-2 pb-2">
      <span class="text-sm text-gray-700 self-center">עדיין לא בוצעו תשלומים</span>
    </div>
  </div>
  </div>

</template>

<script setup lang="ts">

import { IPaymentsResponse, PaymentRequestReason, PaymentRequestStatus } from '@torim-app/types';
import { DateTime } from 'luxon';
import { onMounted, PropType, ref } from 'vue';
import { useAdminStore } from '@app/stores';
import { CustomersApi } from '@app/api/admin';
import { useToast } from 'vue-toastification';
import { isNative } from '@app/utils/capacitor';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { blobToBase64 } from '@app/utils/utils';
import { useDrawer } from '@torim-app/shared-components';
import PaymentDetailsDrawer from '@app/components/admin/pages/customer/PaymentDetailsDrawer.vue';
import CustomerBalance from '@app/components/admin/pages/customer/CustomerBalance.vue';
import { trackEvent } from '@app/composables/posthog';

const adminStore = useAdminStore();
const toast = useToast();
const { openDrawer } = useDrawer();

const props = defineProps({
  payments: {
    type: Array as PropType<Array<IPaymentsResponse>>,
    required: false,
    default: () => []
  },
  clientId: {
    type: String,
    required: true
  }
});

const allPayments = ref<IPaymentsResponse[]>(props.payments ?? []);
const isLoadingPayments = ref<boolean>(!props.payments);

const refundableStatuses = [
  PaymentRequestStatus.Success,
  PaymentRequestStatus.PartiallyRefunded,
  PaymentRequestStatus.Refunded
];

const isRefunded = (payment: IPaymentsResponse) =>
  payment.status === PaymentRequestStatus.Refunded || payment.status === PaymentRequestStatus.PartiallyRefunded;

const selectPayment = (payment: IPaymentsResponse) => {
  if (!refundableStatuses.includes(payment.status)) return;
  trackEvent('customer_payment_details_viewed', { payment_status: payment.status, payment_reason: payment.paymentReason });
  openDrawer(PaymentDetailsDrawer, {
    selectedPayment: payment,
    onRefunded: (updated: IPaymentsResponse) => {
      const idx = allPayments.value.findIndex(p => p._id === updated._id);
      if (idx !== -1) allPayments.value[idx] = { ...allPayments.value[idx], ...updated };
    }
  }, { title: 'פרטי תשלום' });
};

const downloadFile = async (payment: IPaymentsResponse) => {
  if (!payment.ezCountInvoiceLink) return;
  trackEvent('customer_invoice_downloaded');

  try {
    if (isNative()) {
      const response = await fetch(payment.ezCountInvoiceLink);
      if (!response.ok) throw new Error('Network response was not ok');

      // Convert to base64
      const blob = await response.blob();
      const base64Data = await blobToBase64(blob);

      // Save the file to device
      const currentDate = new Date().toLocaleString().replace(/[,:\s\/]/g, '-');
      const fileType = 'pdf';
      const fileName = `חשבונית_${payment._id}`;
      const result = await Filesystem.writeFile({
        path: `${fileName}-${currentDate}.${fileType}`,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      });

      console.log('File downloaded successfully:', result.uri);
      toast.success('הקובץ הורד בהצלחה');
      return result;
    } else {
      const response = await fetch(payment.ezCountInvoiceLink);
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `חשבונית_${payment._id}`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    toast.error('שגיאה בהורדת הקובץ');
    console.error(error);
  }
};

const getTitle = (payment: IPaymentsResponse) => {
  // A Schedule payment (e.g. the balance collected after a bundle/voucher covers part of a
  // booking) is tied to an appointment just like a Visit payment, so label it the same way.
  if (payment.paymentReason === PaymentRequestReason.Visit || payment.paymentReason === PaymentRequestReason.Schedule)
    return `${payment?.appointmentsDates?.length && payment?.appointmentsDates?.length > 1 ? 'ביקורים' : 'ביקור'} ב-${payment.appointmentsDates?.join(', ')}`;
  else if (payment.paymentReason === PaymentRequestReason.Advanced)
    return ` מקדמה עבור ביקור ב-${payment.appointmentsDates?.[0]}`;
  else if (payment.paymentReason === PaymentRequestReason.Voucher)
    return 'תשלום עבור שובר';
  else if (payment.paymentReason === PaymentRequestReason.BundlePack)
    return 'רכישת כרטיסייה';
  else if (payment.settlesCustomerDebt)
    return 'תשלום חוב';
  else return  payment.note ? `תשלום כללי - ${payment.note}` : 'תשלום כללי';
};

const loadPaymentsHistory = async () => {
  try {
    isLoadingPayments.value = true;
    const response = await CustomersApi.getPaymentsInformation(props.clientId);
    allPayments.value = response.paymentsHistory;
  } catch (e) {
    console.log(e);
    toast.error('לא הצלחנו לטעון את היסטוריית התשלומים');
  } finally {
    isLoadingPayments.value = false;
  }
};

onMounted(() => {
  if (!props.payments?.length)
    loadPaymentsHistory();
});
</script>

<style scoped lang="scss">

</style>
